import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SuiFrenImage } from '@suigar/suifrens-display-system';
import type { BullsharkAttributes, BullsharkExpression } from '@suigar/suifrens-display-system';
import type { CapyAttributes, CapyExpression } from '@suigar/suifrens-display-system';
import { cn } from '@/utils';
import { getRandomBullsharkExpression, getRandomCapyExpression } from '@/components/suifrens/expressions';

type SuiFrenImageProps = Parameters<typeof SuiFrenImage>[0];

type ReactiveSuiFrenImageProps = SuiFrenImageProps & {
	reactive?: boolean;
	restoreDelayMs?: number;
	wrapperClassName?: string;
};

export const isBullsharkAttributes = (attributes: BullsharkAttributes | CapyAttributes): attributes is BullsharkAttributes => {
	return 'finStyle' in attributes;
};

export function ReactiveSuiFrenImage({
	attributes,
	accessories,
	incognito,
	logo,
	shadow,
	reactive = false,
	restoreDelayMs = 900,
	wrapperClassName,
}: ReactiveSuiFrenImageProps) {
	const [overrideExpression, setOverrideExpression] = useState<string | null>(null);
	const timeoutRef = useRef<number | null>(null);

	const baseExpression = attributes.expression;
	const displayExpression = (overrideExpression ?? baseExpression) as typeof attributes.expression;

	const displayAttributes = useMemo(() => {
		return {
			...attributes,
			expression: displayExpression,
		} as typeof attributes;
	}, [attributes, displayExpression]);

	const triggerReactiveExpression = useCallback(() => {
		if (!reactive) return;

		const isBullshark = isBullsharkAttributes(attributes);
		const current = (overrideExpression ?? baseExpression) as BullsharkExpression | CapyExpression;

		const nextExpression = isBullshark
			? getRandomBullsharkExpression(current as BullsharkExpression)
			: getRandomCapyExpression(current as CapyExpression);

		setOverrideExpression(nextExpression);

		if (timeoutRef.current) {
			window.clearTimeout(timeoutRef.current);
		}
		timeoutRef.current = window.setTimeout(() => {
			setOverrideExpression(null);
			timeoutRef.current = null;
		}, restoreDelayMs);
	}, [attributes, baseExpression, overrideExpression, reactive, restoreDelayMs]);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				window.clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		setOverrideExpression(null);
	}, [baseExpression]);

	return (
		<div
			className={cn('inline-flex items-center justify-center', reactive && 'cursor-pointer', wrapperClassName)}
			role={reactive ? 'button' : undefined}
			tabIndex={reactive ? 0 : undefined}
			aria-pressed={reactive ? overrideExpression !== null : undefined}
			onClick={reactive ? triggerReactiveExpression : undefined}
			onKeyDown={
				reactive
					? (event) => {
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								triggerReactiveExpression();
							}
						}
					: undefined
			}
		>
			<SuiFrenImage
				attributes={displayAttributes as unknown as BullsharkAttributes | CapyAttributes}
				accessories={accessories}
				incognito={incognito}
				logo={logo}
				shadow={shadow}
			/>
		</div>
	);
}

export default ReactiveSuiFrenImage;
